import React from "react";
import dayjs from "dayjs";

function MessageDetails({
  selectedConversation,
  setView,
  setupDetails,
}) {
  const ticket = selectedConversation?.ticketId;

  const formatStatus = (status) => {
    if (!status) return "Open";
    const statusMap = {
      unassigned: "Unassigned",
      assigned: "Assigned",
      in_progress: "In Progress",
      awaiting_customer: "Awaiting Customer",
      awaiting_third_party: "Awaiting Third Party",
      snoozed: "Snoozed",
      resolved: "Resolved",
      duplicate: "Duplicate",
      merged: "Merged",
      cancelled: "Cancelled",
      reopen: "Reopen",
      closed: "Closed",
    };
    return (
      statusMap[status.toLowerCase()] ||
      status.replace(/_/g, " ").replace(/\b\w/g, (l) => l.toUpperCase())
    );
  };

  const primaryColor =
    setupDetails?.widgetCustomizations?.primaryColor ||
    setupDetails?.widgetCustomizations?.color1 ||
    "#2563eb";

  if (!ticket) return null;

  return (
    <div className="flex flex-col h-full bg-white overflow-hidden">
      {/* Subheader Tabs: Discussion | Details */}
      <div className="flex items-center px-4 border-b border-slate-100 bg-white shrink-0">
        <button
          onClick={() => setView("message")}
          className="py-2.5 px-3 text-xs font-bold transition-all relative border-none bg-transparent cursor-pointer text-slate-400 hover:text-slate-600"
        >
          Discussion
        </button>
        <button
          onClick={() => setView("MessageDetails")}
          className="py-2.5 px-3 text-xs font-bold transition-all relative border-none bg-transparent cursor-pointer text-slate-900"
        >
          Details
          <span
            className="absolute bottom-0 left-2 right-2 h-0.5 rounded-full"
            style={{ backgroundColor: primaryColor }}
          />
        </button>
      </div>

      {/* Details Cards Content */}
      <div className="flex-1 overflow-y-auto p-4 space-y-4 dexkor-scrollbar bg-slate-50/50">
        {/* Ticket Summary Header Card */}
        <div className="bg-white rounded-2xl p-4 border border-slate-200/80 shadow-sm space-y-3">
          <div className="flex items-center justify-between">
            <span className="text-xs font-bold px-2.5 py-1 rounded-lg bg-slate-100 text-slate-800 font-mono">
              #{ticket?.ticketNumber}
            </span>
            <div className="flex items-center gap-2">
              {ticket?.priority && (
                <span className="text-xs font-semibold px-2 py-0.5 rounded-full capitalize bg-blue-50 text-blue-700 border border-blue-100">
                  {ticket.priority}
                </span>
              )}
              <span className="text-xs font-bold px-2.5 py-0.5 rounded-full bg-emerald-50 text-emerald-800 border border-emerald-200">
                {formatStatus(ticket?.status || "open")}
              </span>
            </div>
          </div>

          <div>
            <h3 className="text-base font-bold text-slate-900 leading-snug">
              {ticket?.subject || "No Subject"}
            </h3>
            <p className="text-xs text-slate-400 mt-1">
              Created on {ticket?.createdAt ? dayjs(ticket.createdAt).format("MMM DD, YYYY · hh:mm A") : "—"}
            </p>
          </div>
        </div>

        {/* Description Card */}
        <div className="bg-white rounded-2xl p-4 border border-slate-200/80 shadow-sm space-y-2">
          <h4 className="text-[11px] font-bold text-slate-400 uppercase tracking-wider">
            Description
          </h4>
          <p className="text-xs sm:text-sm text-slate-700 whitespace-pre-wrap leading-relaxed">
            {ticket?.description || "No description provided."}
          </p>
        </div>

        {/* Customer Details Card */}
        <div className="bg-white rounded-2xl p-4 border border-slate-200/80 shadow-sm space-y-2.5">
          <h4 className="text-[11px] font-bold text-slate-400 uppercase tracking-wider">
            Requester Details
          </h4>
          <div className="divide-y divide-slate-100 text-xs text-slate-600">
            <div className="py-2 flex items-center justify-between">
              <span className="text-slate-400">Email</span>
              <span className="font-semibold text-slate-800">{ticket?.email || "—"}</span>
            </div>
            {ticket?.source && (
              <div className="py-2 flex items-center justify-between">
                <span className="text-slate-400">Channel</span>
                <span className="font-semibold text-slate-800 capitalize">{ticket.source}</span>
              </div>
            )}
            {ticket?.lastSender && (
              <div className="py-2 flex items-center justify-between">
                <span className="text-slate-400">Last Message From</span>
                <span className="font-semibold text-slate-800">{ticket.lastSender}</span>
              </div>
            )}
          </div>
        </div>

        {/* SLA & Timeline Card */}
        {(ticket?.slaFirstResponseDue || ticket?.slaResolutionDue) && (
          <div className="bg-white rounded-2xl p-4 border border-slate-200/80 shadow-sm space-y-2.5">
            <div className="flex items-center justify-between">
              <h4 className="text-[11px] font-bold text-slate-400 uppercase tracking-wider">
                SLA Performance
              </h4>
              <span
                className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${
                  ticket?.slaBreached
                    ? "bg-rose-50 text-rose-700 border border-rose-200"
                    : "bg-emerald-50 text-emerald-700 border border-emerald-200"
                }`}
              >
                {ticket?.slaBreached ? "SLA Breached" : "On Track"}
              </span>
            </div>
            <div className="divide-y divide-slate-100 text-xs text-slate-600">
              {ticket?.slaFirstResponseDue && (
                <div className="py-2 flex items-center justify-between">
                  <span className="text-slate-400">First Response Due</span>
                  <span className="font-medium text-slate-800">
                    {dayjs(ticket.slaFirstResponseDue).format("MMM DD, hh:mm A")}
                  </span>
                </div>
              )}
              {ticket?.slaResolutionDue && (
                <div className="py-2 flex items-center justify-between">
                  <span className="text-slate-400">Resolution Due</span>
                  <span className="font-medium text-slate-800">
                    {dayjs(ticket.slaResolutionDue).format("MMM DD, hh:mm A")}
                  </span>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Meta / History Card */}
        <div className="bg-white rounded-2xl p-4 border border-slate-200/80 shadow-sm space-y-2.5">
          <h4 className="text-[11px] font-bold text-slate-400 uppercase tracking-wider">
            Timestamps
          </h4>
          <div className="divide-y divide-slate-100 text-xs text-slate-600">
            <div className="py-2 flex items-center justify-between">
              <span className="text-slate-400">Created</span>
              <span className="font-medium text-slate-800">
                {ticket?.createdAt ? dayjs(ticket.createdAt).format("MMM DD, YYYY · hh:mm A") : "—"}
              </span>
            </div>
            <div className="py-2 flex items-center justify-between">
              <span className="text-slate-400">Last Updated</span>
              <span className="font-medium text-slate-800">
                {ticket?.updatedAt ? dayjs(ticket.updatedAt).format("MMM DD, YYYY · hh:mm A") : "—"}
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default MessageDetails;
